import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { ScheduleManagerPromiseClient } from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_grpc_web_pb'
import {
  GetScheduleRequest,
  ScheduleRecord,
  SchedulesResponse,
  CreateUpdateScheduleRequest,
  DeleteScheduleRequest,
  EnableScheduleRequest,
} from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useGrpcRevalidateByMethod } from 'src/components'

import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { queryParamAsArray } from 'src/Utils/queryParams'
import {
  get_status_display,
  doesScheduleTypeInclude,
} from './SchedulingHelpers'
import { SchedulingRowData } from './SchedulingIndexPage.constants'

export const useSchedule = (): GrpcResponse<ScheduleRecord> => {
  const { response } = useSwrImmutableGrpc(
    ScheduleManagerPromiseClient.prototype.getSchedule,
    new GetScheduleRequest(),
  )
  return NewGrpcResponse(response)
}

export const useSchedules = (): GrpcResponse<SchedulesResponse> => {
  const { response } = useSwrImmutableGrpc(
    ScheduleManagerPromiseClient.prototype.getSchedules,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export const useScheduleCreateOrUpdate = (): ((
  request: CreateUpdateScheduleRequest,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ScheduleManagerPromiseClient)

  return useCallback(
    async (request: CreateUpdateScheduleRequest) => {
      await client.createOrUpdateSchedule(request)
    },
    [client],
  )
}

export const useInvalidateSchedule = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()
  return useCallback(async () => {
    await mutateFunc(ScheduleManagerPromiseClient.prototype.getSchedule)
    await mutateFunc(ScheduleManagerPromiseClient.prototype.getSchedules)
  }, [mutateFunc])
}

export const useDeleteSchedule = (): ((id: string) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ScheduleManagerPromiseClient)
  return useCallback(
    async (id: string) => {
      const req = new DeleteScheduleRequest()
      req.setId(id)
      await client.deleteSchedule(req)
    },
    [client],
  )
}

export const useGetScheduleById = (
  id: string | undefined,
): GrpcResponse<ScheduleRecord> => {
  const request = useMemo(() => {
    if (!id) return null
    const req = new GetScheduleRequest()
    req.setId(id)
    return req
  }, [id])

  const { response } = useSwrImmutableGrpc(
    ScheduleManagerPromiseClient.prototype.getSchedule,
    request as GetScheduleRequest,
    !!request,
  )

  return NewGrpcResponse(response)
}

export const useEnableSchedule = (): ((
  id: string,
  enabled: boolean,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ScheduleManagerPromiseClient)
  return useCallback(
    async (id: string, enabled: boolean) => {
      const req = new EnableScheduleRequest()
      req.setId(id)
      req.setEnabled(enabled)
      await client.enableSchedule(req)
    },
    [client],
  )
}

export const useSearchableSchedules = (
  schedules: SchedulingRowData[],
): SearchableData<SchedulingRowData>[] => {
  const location = useLocation()
  const queryParams = useMemo(
    () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
    [location.search],
  )
  const executionTypes = useMemo(() => {
    return queryParamAsArray(queryParams[FilterParam.EXECUTION_TYPE])
  }, [queryParams])

  const filteredSchedules = useMemo(() => {
    if (!executionTypes || executionTypes.length === 0) return schedules
    return schedules.filter((row) => {
      // row.ScheduleType comes from the protobuf enum, executionTypes are strings from URL
      return executionTypes.some((filter) =>
        doesScheduleTypeInclude(row.ScheduleTypes, parseInt(filter)),
      )
    })
  }, [schedules, executionTypes])

  const searchableSchedules = useMemo(() => {
    return prepareSearchableData(
      filteredSchedules,
      (schedule: SchedulingRowData) => schedule.Id,
      (schedule: SchedulingRowData) => {
        const id = schedule.Id
        const name = schedule.Name
        const executionTypes = schedule.ExecutionTypes
        const { status: statusDisplay } = get_status_display(
          schedule.LastRunStatus,
        )
        const frequency = schedule.Frequency
        const lastRunTime = schedule.LastRunTime
        return [id, name, executionTypes, statusDisplay, frequency, lastRunTime]
      },
    )
  }, [filteredSchedules])

  return searchableSchedules
}
